import { useEffect, useMemo, useState } from "react";

const DENTISTS_URL =
  "https://ingresosolicitudes.dentistacontralor.cl//api/v1/public/dentists?order_by=3";
const DEFAULT_BACKEND_IMAGE =
  "https://ingresosolicitudes.dentistacontralor.cl/img/dentist.png";

function normalizeImage(url) {
  if (!url) return null;
  return url.replace(/\\\//g, "/");
}

function isCustomImage(url) {
  const normalized = normalizeImage(url);
  return normalized && normalized !== DEFAULT_BACKEND_IMAGE ? normalized : null;
}

function initialsFromName(name) {
  return (name || "")
    .split(" ")
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0])
    .join("")
    .toUpperCase();
}

function DentistFallback({ name }) {
  const initials = initialsFromName(name) || "OD";

  return (
    <div className="w-20 h-20 rounded-full bg-gradient-to-br from-sky-500 via-cyan-500 to-teal-500 text-white flex items-center justify-center relative shadow-sm">
      <div className="text-lg font-semibold tracking-wide">{initials}</div>
      <div className="absolute -bottom-1 -right-1 w-8 h-8 rounded-full bg-white border border-sky-100 flex items-center justify-center shadow-sm">
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" className="text-codelco">
          <path
            d="M12 3C8.6 3 6 5.47 6 8.67C6 11.7 7.7 13.7 8.45 15.8C9.02 17.4 9.36 19.2 9.77 20.27C10.08 21.08 10.88 21.5 12 21.5C13.12 21.5 13.92 21.08 14.23 20.27C14.64 19.2 14.98 17.4 15.55 15.8C16.3 13.7 18 11.7 18 8.67C18 5.47 15.4 3 12 3Z"
            fill="currentColor"
          />
          <path
            d="M9.2 8.5C9.2 7.1 10.42 6 12 6C13.58 6 14.8 7.1 14.8 8.5"
            stroke="white"
            strokeWidth="1.5"
            strokeLinecap="round"
          />
        </svg>
      </div>
    </div>
  );
}

function DentistAvatar({ name, image }) {
  const [imageError, setImageError] = useState(false);
  const customImage = isCustomImage(image);

  if (!customImage || imageError) {
    return <DentistFallback name={name} />;
  }

  return (
    <img
      src={customImage}
      alt={name}
      className="w-20 h-20 rounded-full object-cover border border-gray-200 shadow-sm"
      onError={() => setImageError(true)}
    />
  );
}

function ValueCell({ children, href }) {
  if (!children) {
    return <span className="text-gray-300">-</span>;
  }

  if (!href) {
    return <span>{children}</span>;
  }

  return (
    <a href={href} target="_blank" rel="noreferrer" className="text-codelco hover:underline break-all">
      {children}
    </a>
  );
}

export default function OdontologosTratantes() {
  const [dentists, setDentists] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    let ignore = false;

    async function loadDentists() {
      try {
        setLoading(true);
        setError("");

        const response = await fetch(DENTISTS_URL);
        if (!response.ok) {
          throw new Error(`Error ${response.status}`);
        }

        const payload = await response.json();
        if (!ignore) {
          setDentists(Array.isArray(payload.data) ? payload.data : []);
        }
      } catch (err) {
        if (!ignore) {
          setError(err.message || "No se pudo obtener el listado.");
        }
      } finally {
        if (!ignore) {
          setLoading(false);
        }
      }
    }

    loadDentists();

    return () => {
      ignore = true;
    };
  }, []);

  const activeDentists = useMemo(
    () => dentists.filter((dentist) => dentist.is_active !== false),
    [dentists]
  );

  return (
    <section className="px-6 md:px-8 py-8 max-w-6xl mx-auto">
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4 mb-4">
        <div>
          <div className="section-tag">Profesionales habilitados</div>
          <h2 className="section-title mb-2">Odontólogos Tratantes</h2>
          <p className="text-sm text-gray-500">
            Listado público actualizado desde la plataforma del Dentista Contralor.
          </p>
        </div>

        <div className="flex flex-wrap gap-2">
          <a
            href="https://rnpi.superdesalud.gob.cl/"
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center gap-2 rounded-lg bg-green-600 px-4 py-2.5 text-sm font-medium text-white hover:bg-green-700 transition-colors"
          >
            Super Intendencia de Salud
          </a>
          <a
            href="https://dentistacontralor.cl/static/Solicitud-2016.pdf"
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center gap-2 rounded-lg bg-blue-600 px-4 py-2.5 text-sm font-medium text-white hover:bg-blue-700 transition-colors"
          >
            Formulario
          </a>
        </div>
      </div>

      <div className="card overflow-hidden">
        {loading && (
          <div className="px-6 py-12 text-center text-sm text-gray-500">
            Cargando listado de odontólogos tratantes...
          </div>
        )}

        {!loading && error && (
          <div className="px-6 py-12 text-center">
            <p className="text-sm text-red-500 mb-3">
              No fue posible obtener el listado en este momento.
            </p>
            <p className="text-xs text-gray-400">{error}</p>
          </div>
        )}

        {!loading && !error && (
          <>
            <div className="hidden lg:block overflow-x-auto">
              <table className="min-w-full">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    <th className="w-28 px-4 py-4 text-left text-sm font-semibold text-gray-700"></th>
                    <th className="px-4 py-4 text-left text-sm font-semibold text-gray-700">Nombre</th>
                    <th className="px-4 py-4 text-left text-sm font-semibold text-gray-700">Especialidad</th>
                    <th className="px-4 py-4 text-left text-sm font-semibold text-gray-700">Dirección</th>
                    <th className="px-4 py-4 text-left text-sm font-semibold text-gray-700">N° Teléfono</th>
                    <th className="px-4 py-4 text-left text-sm font-semibold text-gray-700">Página Web</th>
                  </tr>
                </thead>
                <tbody>
                  {activeDentists.map((dentist) => (
                    <tr key={dentist.id} className="border-b border-gray-200 align-top">
                      <td className="px-4 py-3">
                        <DentistAvatar name={dentist.name} image={dentist.image} />
                      </td>
                      <td className="px-4 py-4 text-[15px] text-gray-800">{dentist.name}</td>
                      <td className="px-4 py-4 text-[15px] text-gray-700">
                        <ValueCell>{dentist.specialty}</ValueCell>
                      </td>
                      <td className="px-4 py-4 text-[15px] text-gray-700">
                        <ValueCell>{dentist.address}</ValueCell>
                      </td>
                      <td className="px-4 py-4 text-[15px] text-gray-700">
                        <ValueCell href={dentist.phone ? `tel:${dentist.phone.replace(/\s+/g, "")}` : null}>
                          {dentist.phone}
                        </ValueCell>
                      </td>
                      <td className="px-4 py-4 text-[15px] text-gray-700">
                        <ValueCell href={dentist.website}>{dentist.website}</ValueCell>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="lg:hidden divide-y divide-gray-200">
              {activeDentists.map((dentist) => (
                <article key={dentist.id} className="p-4">
                  <div className="flex gap-4">
                    <DentistAvatar name={dentist.name} image={dentist.image} />
                    <div className="min-w-0 flex-1">
                      <h3 className="text-base font-semibold text-gray-800 mb-2">{dentist.name}</h3>
                      <div className="space-y-1.5 text-sm text-gray-600">
                        <p>
                          <span className="font-medium text-gray-800">Especialidad:</span>{" "}
                          <ValueCell>{dentist.specialty}</ValueCell>
                        </p>
                        <p>
                          <span className="font-medium text-gray-800">Dirección:</span>{" "}
                          <ValueCell>{dentist.address}</ValueCell>
                        </p>
                        <p>
                          <span className="font-medium text-gray-800">Teléfono:</span>{" "}
                          <ValueCell href={dentist.phone ? `tel:${dentist.phone.replace(/\s+/g, "")}` : null}>
                            {dentist.phone}
                          </ValueCell>
                        </p>
                        <p>
                          <span className="font-medium text-gray-800">Página Web:</span>{" "}
                          <ValueCell href={dentist.website}>{dentist.website}</ValueCell>
                        </p>
                      </div>
                    </div>
                  </div>
                </article>
              ))}
            </div>
          </>
        )}
      </div>
    </section>
  );
}
